import React from 'react'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'
import { Modal, Button, Form, Col, Table, Row } from '_zari/core'

import { EstadosRequerimiento } from 'constants/index'

import * as RequerimientoService from 'services/requerimiento.service'

// Transiciones válidas según flujo v2
const transicionesValidas = {
  [EstadosRequerimiento.BORRADOR]: [EstadosRequerimiento.PENDIENTE_APROBACION_JEFE],
  [EstadosRequerimiento.PENDIENTE_APROBACION_JEFE]: [
    EstadosRequerimiento.ABIERTO,
    EstadosRequerimiento.OBSERVADO,
    EstadosRequerimiento.RECHAZADO
  ],
  [EstadosRequerimiento.EN_REVISION]: [
    EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN,
    EstadosRequerimiento.ELEVADO_A_GERENCIA,
    EstadosRequerimiento.CAJA_CHICA,
    EstadosRequerimiento.OBSERVADO,
    EstadosRequerimiento.RECHAZADO
  ],
  [EstadosRequerimiento.ELEVADO_A_GERENCIA]: [
    EstadosRequerimiento.APROBADO_POR_GERENCIA,
    EstadosRequerimiento.OBSERVADO,
    EstadosRequerimiento.RECHAZADO
  ],
  [EstadosRequerimiento.APROBADO_POR_GERENCIA]: [],
  [EstadosRequerimiento.CAJA_CHICA]: [
    EstadosRequerimiento.APROBADO_POR_GERENCIA,
    EstadosRequerimiento.OBSERVADO,
    EstadosRequerimiento.RECHAZADO
  ],
  [EstadosRequerimiento.CON_ORDEN_DE_COMPRA]: [EstadosRequerimiento.CERRADO],
  [EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN]: [EstadosRequerimiento.CERRADO],
  [EstadosRequerimiento.OBSERVADO]: [EstadosRequerimiento.PENDIENTE_APROBACION_JEFE, EstadosRequerimiento.ABIERTO],
  // ABIERTO = aprobado por jefe, pendiente recepción logística
  [EstadosRequerimiento.ABIERTO]: [EstadosRequerimiento.EN_REVISION]
}

// Rol responsable por transición (estado destino)
const rolPorEstado = {
  [EstadosRequerimiento.PENDIENTE_APROBACION_JEFE]: 'Oficina',
  [EstadosRequerimiento.ABIERTO]: 'Jefe de Area',
  [EstadosRequerimiento.EN_REVISION]: 'Logistica',
  [EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN]: 'Logistica',
  [EstadosRequerimiento.ELEVADO_A_GERENCIA]: 'Logistica',
  [EstadosRequerimiento.CAJA_CHICA]: 'Logistica',
  [EstadosRequerimiento.APROBADO_POR_GERENCIA]: 'Gerencia',
  [EstadosRequerimiento.CON_ORDEN_DE_COMPRA]: 'Logistica',
  [EstadosRequerimiento.CERRADO]: 'Logistica',
  [EstadosRequerimiento.OBSERVADO]: 'Logistica',
  [EstadosRequerimiento.RECHAZADO]: 'Logistica'
}

const estadosConComentarioObligatorio = [
  EstadosRequerimiento.OBSERVADO,
  EstadosRequerimiento.RECHAZADO,
  EstadosRequerimiento.ELEVADO_A_GERENCIA
]

// Estados donde se muestra la seccion de cotizaciones
const estadosConCotizaciones = [EstadosRequerimiento.EN_REVISION, EstadosRequerimiento.ELEVADO_A_GERENCIA]

// Sección de evaluación por ítem (cantidad almacén / cantidad OC por línea)
// Proveedor y precio se asignan al generar la Orden de Compra, NO aquí.
const EvaluacionSection = React.memo(({ idRequerimiento, nuevoEstado, evaluacionesRef }) => {
  const __zariContext = React.useContext(ZariContext)
  const snackRef = React.useRef(__zariContext.snack)
  snackRef.current = __zariContext.snack

  const [detalles, setDetalles] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const esSoloAlmacen = nuevoEstado === EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN

  React.useEffect(() => {
    if (!idRequerimiento) return
    let cancelled = false
    setLoading(true)
    RequerimientoService.obtenerDetallesEvaluacion(idRequerimiento)
      .then(({ data }) => {
        if (cancelled) return
        const detallesIniciales = ((data && data.detalles) || []).map(d => {
          let cA = d.cantidadAlmacen || 0
          let cOC = d.cantidadOrdenCompra || 0
          if (esSoloAlmacen) {
            cA = d.cantidad
            cOC = 0
          } else if (cA === 0 && cOC === 0) {
            const sugeridoAlmacen = Math.min(d.cantidad, d.stockDisponible || 0)
            cA = sugeridoAlmacen
            cOC = d.cantidad - sugeridoAlmacen
          }
          return {
            ...d,
            cantidadAlmacen: cA,
            cantidadOrdenCompra: cOC
          }
        })
        setDetalles(detallesIniciales)
      })
      .catch(err => {
        if (cancelled) return
        const msg = err?.data?.error || 'Error al cargar evaluación'
        if (snackRef.current) snackRef.current('error', msg)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [idRequerimiento, esSoloAlmacen])

  React.useEffect(() => {
    evaluacionesRef.current = () =>
      detalles.map(d => ({
        _id: d._id,
        cantidadAlmacen: Number(d.cantidadAlmacen) || 0,
        cantidadOrdenCompra: Number(d.cantidadOrdenCompra) || 0
      }))
  }, [detalles, evaluacionesRef])

  const updateDetalle = (id, patch) => {
    setDetalles(prev => prev.map(d => (d._id === id ? { ...d, ...patch } : d)))
  }

  const onChangeAlmacen = (row, val) => {
    const cA = Math.max(0, Math.min(row.cantidad, Number(val) || 0))
    const cOC = row.cantidad - cA
    updateDetalle(row._id, { cantidadAlmacen: cA, cantidadOrdenCompra: cOC })
  }

  const onChangeOC = (row, val) => {
    const cOC = Math.max(0, Math.min(row.cantidad, Number(val) || 0))
    const cA = row.cantidad - cOC
    updateDetalle(row._id, { cantidadAlmacen: cA, cantidadOrdenCompra: cOC })
  }

  if (loading) return <div className='text-center py-2'>Cargando detalles...</div>
  if (detalles.length === 0) return <div className='text-muted py-2'>Sin detalles para evaluar.</div>

  return (
    <div className='mt-3 mb-2'>
      <strong className='d-block mb-2'>
        Evaluación por ítem ({esSoloAlmacen ? 'Todo desde Almacén' : 'División Almacén / Orden de Compra'})
      </strong>
      <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
        <table className='table table-sm table-bordered mb-0' style={{ fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
            <tr>
              <th>Producto</th>
              <th style={{ width: 70 }}>Total</th>
              <th style={{ width: 80 }}>Stock</th>
              <th style={{ width: 90 }}>Almacén</th>
              {!esSoloAlmacen && <th style={{ width: 90 }}>OC</th>}
            </tr>
          </thead>
          <tbody>
            {detalles.map(row => {
              const stock = row.stockDisponible || 0
              const excedeStock = row.cantidadAlmacen > stock
              return (
                <tr key={row._id}>
                  <td>{row.nombreProducto}</td>
                  <td className='text-right'>
                    {row.cantidad} {row.unidadMedida || ''}
                  </td>
                  <td
                    className='text-right'
                    style={{ color: stock > 0 ? '#155724' : '#6c757d', fontWeight: 500 }}
                    title='Stock disponible en la sede actual'
                  >
                    {stock}
                  </td>
                  <td>
                    <input
                      type='number'
                      min={0}
                      max={row.cantidad}
                      className='form-control form-control-sm'
                      value={row.cantidadAlmacen}
                      disabled={esSoloAlmacen}
                      onChange={e => onChangeAlmacen(row, e.target.value)}
                      style={excedeStock ? { borderColor: '#dc3545' } : undefined}
                      title={excedeStock ? `Excede stock disponible (${stock})` : ''}
                    />
                  </td>
                  {!esSoloAlmacen && (
                    <td>
                      <input
                        type='number'
                        min={0}
                        max={row.cantidad}
                        className='form-control form-control-sm'
                        value={row.cantidadOrdenCompra}
                        onChange={e => onChangeOC(row, e.target.value)}
                      />
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <small className='text-muted d-block mt-1'>
        Almacén + OC debe igualar al total. Proveedor y precio se asignan al generar la Orden de Compra.
      </small>
    </div>
  )
})

const CotizacionesSection = ({ idRequerimiento, estadoActual, cotizaciones, setCotizaciones, onReload }) => {
  const __zariContext = React.useContext(ZariContext)
  const [comentarioLogistica, setComentarioLogistica] = React.useState('')
  const [agregando, setAgregando] = React.useState(false)
  const [pdfPreviewUrl, setPdfPreviewUrl] = React.useState('')
  const [showPreview, setShowPreview] = React.useState(false)
  const fileInputRef = React.useRef(null)

  const esGerencia = estadoActual === EstadosRequerimiento.ELEVADO_A_GERENCIA

  const handleAgregar = React.useCallback(() => {
    const file = fileInputRef.current?.files?.[0]
    if (!file) {
      __zariContext.snack('error', 'Seleccione un archivo PDF.')
      return
    }
    setAgregando(true)
    RequerimientoService.agregarCotizacion(idRequerimiento, file, comentarioLogistica.trim() || null)
      .then(() => {
        setComentarioLogistica('')
        if (fileInputRef.current) fileInputRef.current.value = ''
        onReload()
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
      .finally(() => setAgregando(false))
  }, [__zariContext, idRequerimiento, comentarioLogistica, onReload])

  const handleEliminar = React.useCallback(
    idCotizacion => {
      RequerimientoService.eliminarCotizacion(idCotizacion)
        .then(() => onReload())
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext, onReload]
  )

  const handleSeleccionar = React.useCallback(
    idCotizacion => {
      const yaSeleccionada = cotizaciones.find(c => c.flagSeleccionada)
      if (yaSeleccionada && yaSeleccionada._id !== idCotizacion) {
        if (!window.confirm('Ya hay una cotización seleccionada. ¿Desea cambiarla?')) return
      }
      RequerimientoService.seleccionarCotizacion(idRequerimiento, idCotizacion)
        .then(() => onReload())
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext, idRequerimiento, cotizaciones, onReload]
  )

  const handlePreview = React.useCallback(
    idCotizacion => {
      __zariContext.loader.open('Cargando PDF...')
      RequerimientoService.descargarCotizacion(idCotizacion)
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          setPdfPreviewUrl(url)
          setShowPreview(true)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => __zariContext.loader.close())
    },
    [__zariContext]
  )

  const handleClosePreview = React.useCallback(() => {
    setShowPreview(false)
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl)
      setPdfPreviewUrl('')
    }
  }, [pdfPreviewUrl])

  return (
    <div className='mt-3 mb-2'>
      <strong className='d-block mb-2'>Cotizaciones (Opcional) ({cotizaciones.length})</strong>

      {cotizaciones.length > 0 && (
        <div className='table-responsive mb-2'>
          <table className='table table-sm table-bordered mb-0'>
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Comentario</th>
                <th>Usuario</th>
                <th style={{ width: 140 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cotizaciones.map(cot => (
                <tr
                  key={cot._id}
                  style={cot.flagSeleccionada ? { backgroundColor: '#FFF2CC' } : undefined}
                >
                  <td>
                    {cot.nombreArchivo}
                    {cot.flagSeleccionada && <span className='badge badge-warning ml-2'>FAVORITA</span>}
                  </td>
                  <td>{cot.comentarioLogistica || '--'}</td>
                  <td style={{ fontSize: '0.8rem' }}>{cot.nombreUsuario || cot._idUsuario}</td>
                  <td>
                    <div className='d-flex'>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        className='mr-1'
                        title='Ver PDF'
                        onClick={() => handlePreview(cot._id)}
                      >
                        <i className='fa fa-file-pdf' />
                      </Button>
                      {esGerencia && (
                        <Button
                          size='sm'
                          variant={cot.flagSeleccionada ? 'warning' : 'outline-warning'}
                          className='mr-1'
                          title='Seleccionar como favorita'
                          onClick={() => handleSeleccionar(cot._id)}
                        >
                          <i className='fa fa-star' />
                        </Button>
                      )}
                      {!esGerencia && (
                        <>
                          <Button
                            size='sm'
                            variant={cot.flagSeleccionada ? 'warning' : 'outline-warning'}
                            className='mr-1'
                            title='Marcar como favorita'
                            onClick={() => handleSeleccionar(cot._id)}
                          >
                            <i className='fa fa-star' />
                          </Button>
                          <Button
                            size='sm'
                            variant='outline-danger'
                            title='Eliminar'
                            onClick={() => handleEliminar(cot._id)}
                          >
                            <i className='fa fa-trash' />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!esGerencia && (
        <div className='border rounded p-2'>
          <Row>
            <Col md={5}>
              <input
                ref={fileInputRef}
                type='file'
                accept='application/pdf'
                className='form-control form-control-sm'
                disabled={agregando}
              />
            </Col>
            <Col md={5}>
              <Form.InputText
                placeholder='Comentario (opcional)'
                value={comentarioLogistica}
                onChange={e => setComentarioLogistica(e.target.value)}
                disabled={agregando}
              />
            </Col>
            <Col md={2}>
              <Button size='sm' onClick={handleAgregar} disabled={agregando} className='w-100'>
                Agregar
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {showPreview && (
        <Modal show={showPreview} size='lg'>
          <Modal.Header>
            <Modal.Title>Previsualización de Cotización</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: '70vh' }}>
            <iframe src={pdfPreviewUrl} style={{ width: '100%', height: '100%', border: 'none' }} title='PDF Preview' />
          </Modal.Body>
          <Modal.Footer>
            <Button outline color='secondary' onClick={handleClosePreview}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

// CF-04: Section showing elevation comment + adjunto for gerencia view
const ElevacionInfoSection = ({ idRequerimiento }) => {
  const __zariContext = React.useContext(ZariContext)
  const [elevacionInfo, setElevacionInfo] = React.useState(null)
  const [adjuntoPreviewUrl, setAdjuntoPreviewUrl] = React.useState('')
  const [showAdjuntoPreview, setShowAdjuntoPreview] = React.useState(false)
  const [adjuntoTipo, setAdjuntoTipo] = React.useState('')

  React.useEffect(() => {
    if (!idRequerimiento) return
    RequerimientoService.obtenerTrazabilidadxId(idRequerimiento)
      .then(({ data }) => {
        // Find the elevation event (estadoNuevo = ELE)
        const elevacion = data.find(t => t.estadoNuevo === EstadosRequerimiento.ELEVADO_A_GERENCIA)
        if (elevacion) setElevacionInfo(elevacion)
      })
      .catch(() => {})
  }, [idRequerimiento])

  const handleVerAdjunto = React.useCallback(() => {
    if (!elevacionInfo || !elevacionInfo.adjuntoRuta) return
    __zariContext.loader.open('Cargando adjunto...')
    RequerimientoService.descargarAdjuntoTrazabilidad(elevacionInfo._id)
      .then(({ data }) => {
        const ext = elevacionInfo.adjuntoNombre.split('.').pop().toLowerCase()
        const tipo = ext === 'pdf' ? 'application/pdf' : 'image/png'
        setAdjuntoTipo(ext)
        const url = URL.createObjectURL(new Blob([data], { type: tipo }))
        setAdjuntoPreviewUrl(url)
        setShowAdjuntoPreview(true)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error || 'Error al cargar adjunto'))
      .finally(() => __zariContext.loader.close())
  }, [__zariContext, elevacionInfo])

  const handleCloseAdjunto = React.useCallback(() => {
    setShowAdjuntoPreview(false)
    if (adjuntoPreviewUrl) {
      URL.revokeObjectURL(adjuntoPreviewUrl)
      setAdjuntoPreviewUrl('')
    }
  }, [adjuntoPreviewUrl])

  if (!elevacionInfo) return null

  return (
    <div className='mb-3 border rounded p-2' style={{ backgroundColor: '#f8f9fa' }}>
      <strong className='d-block mb-1'>Información</strong>
      <div className='mb-1'>
        <small className='text-muted'>Comentario:</small>
        <p className='mb-1' style={{ whiteSpace: 'pre-wrap' }}>{elevacionInfo.comentario || '--'}</p>
      </div>
      {elevacionInfo.adjuntoNombre && (
        <div className='d-flex align-items-center'>
          <small className='text-muted mr-2'>Adjunto: {elevacionInfo.adjuntoNombre}</small>
          <Button size='sm' variant='outline-info' title='Ver adjunto' onClick={handleVerAdjunto}>
            <i className='fa fa-eye' />
          </Button>
        </div>
      )}

      {showAdjuntoPreview && (
        <Modal show={showAdjuntoPreview} size='lg'>
          <Modal.Header>
            <Modal.Title>Adjunto: {elevacionInfo.adjuntoNombre}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: '70vh' }}>
            {adjuntoTipo === 'pdf' ? (
              <iframe src={adjuntoPreviewUrl} style={{ width: '100%', height: '100%', border: 'none' }} title='Adjunto PDF' />
            ) : (
              <div className='text-center' style={{ height: '100%', overflow: 'auto' }}>
                <img src={adjuntoPreviewUrl} alt='Adjunto' style={{ maxWidth: '100%' }} />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button outline color='secondary' onClick={handleCloseAdjunto}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

const CambiarEstadoModal = ({ show, handleClose, idRequerimiento, estadoActual, reloadTable }) => {
  const __zariContext = React.useContext(ZariContext)

  const [cotizaciones, setCotizaciones] = React.useState([])
  const [loadingCotizaciones, setLoadingCotizaciones] = React.useState(false)
  const adjuntoInputRef = React.useRef(null)
  const evaluacionesRef = React.useRef(() => [])

  const mostrarCotizaciones = estadosConCotizaciones.includes(estadoActual)
  const esGerencia = estadoActual === EstadosRequerimiento.ELEVADO_A_GERENCIA

  const cargarCotizaciones = React.useCallback(() => {
    if (!idRequerimiento || !mostrarCotizaciones) return
    setLoadingCotizaciones(true)
    RequerimientoService.obtenerCotizacionesxId(idRequerimiento)
      .then(({ data }) => setCotizaciones(data))
      .catch(() => setCotizaciones([]))
      .finally(() => setLoadingCotizaciones(false))
  }, [idRequerimiento, mostrarCotizaciones])

  React.useEffect(() => {
    cargarCotizaciones()
  }, [cargarCotizaciones])

  const estadosDisponibles = React.useMemo(() => {
    const ids = transicionesValidas[estadoActual] || []
    return ids.map(id => EstadosRequerimiento.__dictionary[id]).filter(Boolean)
  }, [estadoActual])

  const formInitialValues = React.useMemo(() => {
    const primerEstado = estadosDisponibles.length === 1 ? estadosDisponibles[0].id : ''
    return {
      estado: primerEstado,
      accion: '',
      rolUsuario: primerEstado ? rolPorEstado[primerEstado] || 'Oficina' : 'Oficina',
      comentario: '',
      destino: ''
    }
  }, [estadosDisponibles])

  const onSubmit = React.useCallback(
    (data, { setSubmitting }) => {
      if (!data.estado) {
        __zariContext.snack('error', 'Seleccione un estado.')
        setSubmitting(false)
        return
      }

      if (estadosConComentarioObligatorio.includes(data.estado) && !data.comentario.trim()) {
        const msg = data.estado === EstadosRequerimiento.ELEVADO_A_GERENCIA
          ? 'El comentario es obligatorio al elevar a gerencia.'
          : 'El comentario es obligatorio al observar o rechazar.'
        __zariContext.snack('error', msg)
        setSubmitting(false)
        return
      }

      // Validar formato adjunto si se proporcionó
      const adjuntoFile = adjuntoInputRef.current?.files?.[0]
      if (adjuntoFile) {
        const ext = adjuntoFile.name.split('.').pop().toLowerCase()
        if (!['pdf', 'png', 'doc', 'docx'].includes(ext)) {
          __zariContext.snack('error', 'Solo se aceptan archivos .pdf, .doc, .docx y .png.')
          setSubmitting(false)
          return
        }
      }

      __zariContext.loader.open('Actualizando estado de requerimiento')

      // Evaluación por ítem (solo aplica al salir de EN_REVISION hacia almacén / gerencia)
      let evaluacionesArr = null
      if (
        estadoActual === EstadosRequerimiento.EN_REVISION &&
        (data.estado === EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN ||
          data.estado === EstadosRequerimiento.ELEVADO_A_GERENCIA ||
          data.estado === EstadosRequerimiento.CAJA_CHICA)
      ) {
        evaluacionesArr = evaluacionesRef.current ? evaluacionesRef.current() : []

        if (!evaluacionesArr || evaluacionesArr.length === 0) {
          __zariContext.snack('error', 'No hay ítems para evaluar.')
          setSubmitting(false)
          return
        }

        for (const ev of evaluacionesArr) {
          const cA = Number(ev.cantidadAlmacen) || 0
          const cOC = Number(ev.cantidadOrdenCompra) || 0
          if (cA === 0 && cOC === 0) {
            __zariContext.snack('error', 'Hay ítems sin cantidad asignada. Distribuya la cantidad entre Almacén y OC.')
            setSubmitting(false)
            return
          }
          if (data.estado === EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN && cOC > 0) {
            __zariContext.snack(
              'error',
              'Para "Atendido desde Almacén" todos los ítems deben ir 100% a almacén. Use "Elevar a Gerencia" si hay OC.'
            )
            setSubmitting(false)
            return
          }
        }
      }

      // Use FormData to support file upload (CF-02)
      const formData = new FormData()
      formData.append('estado', data.estado)
      formData.append('accion', data.accion || '')
      formData.append('rolUsuario', data.rolUsuario)
      if (data.comentario) formData.append('comentario', data.comentario)
      if (data.destino) formData.append('destino', data.destino)
      if (adjuntoFile) formData.append('adjunto', adjuntoFile)
      if (evaluacionesArr) formData.append('evaluaciones', JSON.stringify(evaluacionesArr))

      RequerimientoService.actualizarxIdAsCambioEstadoConTrazabilidadFormData(idRequerimiento, formData)
        .then(() => {
          __zariContext.snack('success', 'Estado actualizado correctamente.')
          reloadTable()
          handleClose()
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
        .finally(() => {
          __zariContext.loader.close()
        })
    },
    [__zariContext, idRequerimiento, reloadTable, handleClose]
  )

  const mostrarEvaluacion = estadoActual === EstadosRequerimiento.EN_REVISION

  return (
    <Modal show={show} size={mostrarEvaluacion ? 'xl' : mostrarCotizaciones || esGerencia ? 'lg' : 'md'}>
      <Modal.Header>
        <Modal.Title>Cambiar estado</Modal.Title>
      </Modal.Header>
      <Formik initialValues={formInitialValues} onSubmit={onSubmit} enableReinitialize>
        {({ errors, touched, isSubmitting, handleSubmit, values, setFieldValue }) => (
          <>
            <Modal.Body>
              {/* CF-04: Show elevation info for gerencia */}
              {esGerencia && <ElevacionInfoSection idRequerimiento={idRequerimiento} />}

              <Form>
                <Form.Group>
                  <Form.Label text='ESTADO ACTUAL' md={4} />
                  <Col md={8}>
                    <Form.InputText
                      disabled
                      value={
                        estadoActual && EstadosRequerimiento.__dictionary[estadoActual]
                          ? EstadosRequerimiento.__dictionary[estadoActual].nombre
                          : estadoActual || ''
                      }
                    />
                  </Col>
                </Form.Group>
                <Form.Group>
                  <Form.Label text='NUEVO ESTADO' htmlFor='select-estado' md={4} />
                  <Col md={8}>
                    <Form.Select
                      id='select-estado'
                      disabled={isSubmitting}
                      value={values.estado}
                      onChange={e => {
                        const nuevoEstado = e.target.value
                        setFieldValue('estado', nuevoEstado)
                        setFieldValue('rolUsuario', rolPorEstado[nuevoEstado] || 'Oficina')
                      }}
                    >
                      {estadosDisponibles.length !== 1 && <option value=''>Seleccionar</option>}
                      {estadosDisponibles.map(est => (
                        <option key={est.id} value={est.id}>
                          {est.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Form.Group>
                <Form.Group>
                  <Form.Label text='ROL' htmlFor='select-rol' md={4} />
                  <Col md={8}>
                    <Form.Select name='rolUsuario' disabled id='select-rol' value={values.rolUsuario}>
                      <option value='Oficina'>Oficina</option>
                      <option value='Jefe de Area'>Jefe de Área</option>
                      <option value='Logistica'>Logística</option>
                      <option value='Gerencia'>Gerencia</option>
                      <option value='Administrador'>Administrador</option>
                    </Form.Select>
                  </Col>
                </Form.Group>
                <Form.Group>
                  <Form.Label text='COMENTARIO' htmlFor='input-comentario' md={4} />
                  <Col md={8}>
                    <textarea
                      name='comentario'
                      className='form-control'
                      disabled={isSubmitting}
                      id='input-comentario'
                      rows={3}
                      value={values.comentario}
                      onChange={e => setFieldValue('comentario', e.target.value)}
                      placeholder={
                        estadosConComentarioObligatorio.includes(values.estado)
                          ? 'Comentario obligatorio'
                          : 'Comentario opcional'
                      }
                    />
                  </Col>
                </Form.Group>
                {/* CF-02: Informe - visible para logística en revisión (oculto si destino es ADA) */}
                {estadoActual === EstadosRequerimiento.EN_REVISION && values.estado !== EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN && (
                  <Form.Group>
                    <Form.Label text='INFORME' md={4} />
                    <Col md={8}>
                      <input
                        ref={adjuntoInputRef}
                        type='file'
                        accept='.pdf,.png,.doc,.docx'
                        className='form-control form-control-sm'
                        disabled={isSubmitting}
                      />
                      <small className='text-muted'>PDF, Word o PNG (1 archivo)</small>
                    </Col>
                  </Form.Group>
                )}
              </Form>

              {estadoActual === EstadosRequerimiento.EN_REVISION &&
                (values.estado === EstadosRequerimiento.ELEVADO_A_GERENCIA ||
                  values.estado === EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN ||
                  values.estado === EstadosRequerimiento.CAJA_CHICA) && (
                  <EvaluacionSection
                    idRequerimiento={idRequerimiento}
                    nuevoEstado={values.estado}
                    evaluacionesRef={evaluacionesRef}
                  />
                )}

              {mostrarCotizaciones && !loadingCotizaciones && values.estado !== EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN && (
                <CotizacionesSection
                  idRequerimiento={idRequerimiento}
                  estadoActual={estadoActual}
                  cotizaciones={cotizaciones}
                  setCotizaciones={setCotizaciones}
                  onReload={cargarCotizaciones}
                />
              )}
              {mostrarCotizaciones && loadingCotizaciones && values.estado !== EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN && (
                <div className='text-center py-2'>Cargando cotizaciones...</div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button outline color='secondary' onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !values.estado}>
                Guardar
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default React.memo(CambiarEstadoModal)
